import React, { FC, useState, useCallback } from 'react';
import { Button } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/Common/UIKit/Input/Input';
import { StatusMessage } from '@/components/Common/CodeAuthenticator';
import styles from './OTPAuthEmailBox.module.scss';
import OPTIONS from '@/components/AdminPanel/PolicyEditor/PolicySettings/PolicySection/PolicyTagInputRow/options.ts';

interface OTPAuthEmailBoxProps {
    recipientEmail: string;
    onEmailSubmit: (email: string) => void;
    isLoading?: boolean;
    errorMessages?: StatusMessage[];
    textSource: 'phone' | 'email' | null;
}

const OTPAuthEmailBox: FC<OTPAuthEmailBoxProps> = observer(({
    recipientEmail,
    onEmailSubmit,
    isLoading,
    errorMessages = [],
    textSource,
}) => {
    const { t } = useTranslation();
    const nameSpace = 'recipientPage';
    const [email, setEmail] = useState(recipientEmail);
    const [error, setError] = useState(false);
    const [isDebounced, setIsDebounced] = useState(false);

    const handleSubmit = useCallback(() => {
        if (isDebounced) return;
        
        const isValid = email.match(OPTIONS.emailAddresses.validationRegexp);
        if (isValid) {
            setIsDebounced(true);
            onEmailSubmit(email);
            // Re-enable button after 3 seconds
            setTimeout(() => {
                setIsDebounced(false);
            }, 3000);
        } else {
            setError(true);
        }
    }, [email, isDebounced, onEmailSubmit]);

    return (
        <div className={styles.emailBoxContainer} data-testid="recipientPage_auth_emailBoxContainer">
            <h2 className={styles.title} data-testid="recipientPage_auth_emailTitle">
                {t(`${nameSpace}.OTPSteps.enterEmailTitle`)}
            </h2>
            <p className={styles.description} data-testid="recipientPage_auth_emailDescription">
                {textSource === 'phone'
                    ? t(`${nameSpace}.OTPSteps.enterEmailFromPhoneDescription`)
                    : t(`${nameSpace}.OTPSteps.enterEmailDescription`)}
            </p>
            <div className={styles.inputWrapper} data-testid="recipientPage_auth_emailInputWrapper">
                <Input
                    placeholder={t(`${nameSpace}.OTPSteps.emailPlaceholder`)}
                    className={styles.emailInput}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    status={error ? 'error' : undefined}
                    suffix={<MailOutlined className={styles.emailIcon} />}
                    data-testid="recipientPage_auth_emailInput"
                />
            </div>
            <Button
                type="primary"
                className={styles.sendButton}
                onClick={handleSubmit}
                loading={isLoading || isDebounced}
                disabled={isLoading || isDebounced}
                data-testid="recipientPage_auth_sendCodeButton"
            >
                {t(`${nameSpace}.OTPSteps.sendCode`)}
            </Button>
        </div>
    );
});

export default OTPAuthEmailBox;
